import dayjs from "dayjs"
import type { RefObject } from "react"
import {
  useRef,
  useLayoutEffect,
} from "react"
import type { VariableSizeList } from "react-window"
import { graphEndPadding } from "./useGraphBounds"

export function usePreserveScrollPosition(
  calculatedWidth: number,
  graphEnd: string,
  pointsPerHour: number,
  listRef: RefObject<VariableSizeList | null>,
  scrollPositionRef: RefObject<number>,
) {
  const previousGraphEndRef = useRef<string>(graphEnd)
  const previousCalculatedWidthRef = useRef<number>(calculatedWidth)
  const previousPointsPerHourRef = useRef<number>(pointsPerHour)

  // preserve the user's scroll position when the graph end changes
  useLayoutEffect(
    () => {
      const scrollPosition = scrollPositionRef.current

      const diffInHours = Math.abs(
        dayjs(graphEnd).diff(
          previousGraphEndRef.current,
          "hours",
          true,
        ),
      )

      const diffInPixels = diffInHours * previousPointsPerHourRef.current

      // if the user is not scrolled to "now" then preserve their position
      if (scrollPosition > 0) {
        listRef.current?.scrollTo(scrollPosition + diffInPixels)
      }

      previousGraphEndRef.current = graphEnd
    },
    [
      graphEnd,
      listRef,
      scrollPositionRef,
      previousPointsPerHourRef,
    ],
  )

  // preserves the user's scroll position when the zoom level changes
  useLayoutEffect(
    () => {
      const scrollPosition = scrollPositionRef.current

      // do not maintain graph center if the user is scrolled to "now"
      if (!(scrollPosition > 0)) {
        return
      }

      const halfWidth = previousCalculatedWidthRef.current / 2
      const ratio = previousPointsPerHourRef.current / pointsPerHour
      const newScrollPosition = Math.max(
        0,
        ((scrollPosition - graphEndPadding + halfWidth) / ratio) + graphEndPadding - halfWidth,
      )

      listRef.current?.scrollTo(newScrollPosition)
      previousPointsPerHourRef.current = pointsPerHour
    },
    [
      pointsPerHour,
      listRef,
      scrollPositionRef,
      previousPointsPerHourRef,
      previousCalculatedWidthRef,
    ],
  )

  // preserves the user's scroll position when the window size changes
  useLayoutEffect(
    () => {
      const scrollPosition = scrollPositionRef.current

      // do not maintain graph center if the user is scrolled to "now"
      if (!(scrollPosition > 0)) {
        return
      }

      const previousHalfWidth = previousCalculatedWidthRef.current / 2
      const halfWidth = calculatedWidth / 2
      const ratio = previousCalculatedWidthRef.current / calculatedWidth
      const newScrollPosition = Math.max(
        0,
        ((scrollPosition - graphEndPadding + previousHalfWidth) / ratio) + graphEndPadding - halfWidth,
      )

      listRef.current?.scrollTo(newScrollPosition)
      previousCalculatedWidthRef.current = calculatedWidth
    },
    [
      calculatedWidth,
      listRef,
      scrollPositionRef,
      previousPointsPerHourRef,
    ],
  )
}
